import React, { useState } from 'react';
import styled from 'astroturf/react';
import { Modal, ModalContent } from 'js/design_system/Modal';
import { Button } from 'js/design_system/Button';
import { Input } from 'js/design_system/forms/Input';
import fetchWithCsrf from 'js/utils/fetch_with_csrf';
import { useToast } from 'js/design_system/toasts/hooks';
import { Colors } from 'js/design_system/Color';

const ModalWrapper = styled.div`
  text-align: left;
  margin-bottom: var(--spacing-lg-64);
`;

const ModalTitle = styled.h2`
  font-size: 28px;
  font-weight: bold;
  margin-bottom: var(--spacing-sm-24);
  color: var(--text-primary);
  line-height: 1.3;
  margin: 0 0 var(--spacing-sm-24) 0;
`;

const FreeText = styled.span`
  color: var(--primary-brand);
`;

const AuthorName = styled.span`
  font-weight: bold;
`;

const FormRow = styled.div`
  font-size: 18px;
  margin-bottom: var(--spacing-sm-24);
  color: var(--text-primary);
  line-height: 1.5;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--spacing-xs-4);
`;

const ToggleButtonGroup = styled.div`
  display: inline-flex;
  align-items: center;
  margin: 0 var(--spacing-xs-8);
`;

const ToggleContainer = styled.div`
  display: flex;
  border: 1px solid var(--stroke-light);
  border-radius: var(--border-radius-radius-md);
  overflow: hidden;
`;

const ToggleButton = styled.button`
  padding: var(--spacing-xs-8) var(--spacing-xs-16);
  font-size: 18px;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: var(--background-white);
  color: var(--text-secondary);

  &.active {
    background-color: var(--button-primary-fill) !important;
    color: var(--button-primary-text) !important;
  }

  &.first {
    border-right: 1px solid var(--stroke-light);
  }
`;

const PositionDropdown = styled.select`
  display: inline-block;
  padding: var(--spacing-xs-8) var(--spacing-xs-16);
  border: 1px solid var(--stroke-light);
  border-radius: var(--border-radius-radius-md);
  background-color: var(--background-white);
  color: var(--text-secondary);
  font-size: 18px;
  margin: 0 var(--spacing-xs-4);
  outline: none;
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e");
  background-position: right 12px center;
  background-repeat: no-repeat;
  background-size: 16px;
  padding-right: 40px;
`;

const LookingIntoText = styled.p`
  font-size: 18px;
  margin-bottom: var(--spacing-xs-16);
  color: var(--text-primary);
  line-height: 1.5;
  margin: 0 0 var(--spacing-xs-16) 0;
`;

const InputContainer = styled.div`
  margin-bottom: var(--spacing-sm-24);
`;

const AnonymousSection = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: var(--spacing-sm-32);
`;

const AnonymousLabel = styled.label`
  display: flex;
  align-items: center;
  cursor: pointer;
`;

const SwitchContainer = styled.div`
  position: relative;
  margin-right: var(--spacing-xs-12);
`;

const HiddenCheckbox = styled.input`
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
`;

const SwitchTrack = styled.div`
  width: 48px;
  height: 24px;
  border-radius: 12px;
  transition: background-color 0.3s ease;
  position: relative;
  cursor: pointer;
  background-color: var(--stroke-light);

  &.active {
    background-color: var(--button-primary-fill) !important;
  }
`;

const SwitchThumb = styled.div`
  width: 20px;
  height: 20px;
  background-color: var(--background-white);
  border-radius: 50%;
  transition: transform 0.3s ease;
  position: absolute;
  top: 2px;
  transform: translateX(2px);

  &.active {
    transform: translateX(24px) !important;
  }
`;

const AnonymousText = styled.span`
  font-size: 18px;
  font-weight: 500;
  color: var(--text-primary);
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-xs-16);
`;

interface FriendlySutdModalProps {
  open: boolean;
  onClose: () => void;
  authorName: string;
  authorId: number;
  workId: number;
  onReadPaper: () => void;
}

export const FriendlySutdModal: React.FC<FriendlySutdModalProps> = ({
  open,
  onClose,
  authorName,
  authorId,
  workId,
  onReadPaper,
}) => {
  // User role options using UserTaggings system (matches the standard survey)
  const roleOptions = [
    { name: 'undergrad', label: 'Undergraduate student' },
    { name: 'graduate', label: 'Graduate or professional degree student' },
    {
      name: 'faculty',
      label: 'Academic faculty, researcher, or retired academic',
    },
    {
      name: 'profession',
      label: 'Public or private sector professional, or self-employed',
    },
    { name: 'retiree', label: 'Retiree (non-academic)' },
    { name: 'other', label: 'Other' },
  ];
  const [formData, setFormData] = useState({
    published: false,
    role: 'graduate', // Default to graduate student
    additionalInfo: '',
    anonymous: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const toast = useToast();

  const handleSubmitAndReadPaper = async () => {
    // Check if topic field is required and empty
    if (!formData.additionalInfo.trim()) {
      toast.errorToast('Please tell us what topic you were looking into');
      return;
    }

    setIsSubmitting(true);

    try {
      // Submit profile data to the API
      await fetchWithCsrf('/api/v0/friendly_sutd_profile', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          profile: {
            published: formData.published,
            role: formData.role,
            topic: formData.additionalInfo.trim() || null,
            anonymous: formData.anonymous,
            work_id: workId,
            author_id: authorId,
          },
        }),
      });

      // Show success toast based on whether it was anonymous
      if (formData.anonymous) {
        toast.infoToast('Anonymous note sent');
      } else {
        toast.infoToast('Thank you sent');
      }

      // Call the original onReadPaper callback
      onReadPaper();
    } catch (_error) {
      toast.errorToast('Something went wrong. Please try again.');
      // Don't proceed to paper reading if there's an error
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkip = () => {
    toast.infoToast('Skipped');
    onReadPaper();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      showHeader={false}
      containerStyle={{
        borderRadius: '16px',
        padding: '24px',
        maxWidth: '588px',
        width: '100%',
        backgroundColor: '#ffffff',
      }}
    >
      <ModalContent style={{ padding: 0 }}>
        <ModalWrapper>
          <ModalTitle>
            This paper is <FreeText>free</FreeText> thanks to{' '}
            <AuthorName>{authorName}</AuthorName>.
          </ModalTitle>

          <FormRow>
            <span>Thanks for sharing your work! I'm an</span>
            <ToggleButtonGroup>
              <ToggleContainer>
                <ToggleButton
                  type="button"
                  onClick={() => {
                    setFormData({ ...formData, published: false });
                  }}
                  className={`${
                    !formData.published ? 'active' : ''
                  } first`.trim()}
                  style={{
                    backgroundColor: !formData.published
                      ? Colors.PrimaryBlue
                      : Colors.White,
                    color: !formData.published ? Colors.White : Colors.GrayDark,
                  }}
                >
                  Unpublished
                </ToggleButton>
                <ToggleButton
                  type="button"
                  onClick={() => {
                    setFormData({ ...formData, published: true });
                  }}
                  className={formData.published ? 'active' : ''}
                  style={{
                    backgroundColor: formData.published
                      ? Colors.PrimaryBlue
                      : Colors.White,
                    color: formData.published ? Colors.White : Colors.GrayDark,
                  }}
                >
                  Published
                </ToggleButton>
              </ToggleContainer>
            </ToggleButtonGroup>
            <PositionDropdown
              value={formData.role}
              onChange={(e) =>
                setFormData({ ...formData, role: e.target.value })
              }
            >
              {roleOptions.map((option) => (
                <option key={option.name} value={option.name}>
                  {option.label}
                </option>
              ))}
            </PositionDropdown>
          </FormRow>

          <LookingIntoText>
            and I came across it while looking into
          </LookingIntoText>

          <InputContainer>
            <Input
              value={formData.additionalInfo}
              onChange={(e) =>
                setFormData({ ...formData, additionalInfo: e.target.value })
              }
              placeholder="e.g. catalpa growth zones (required)"
            />
          </InputContainer>

          <AnonymousSection>
            <AnonymousLabel>
              <SwitchContainer>
                <HiddenCheckbox
                  type="checkbox"
                  checked={formData.anonymous}
                  onChange={(e) => {
                    setFormData({ ...formData, anonymous: e.target.checked });
                  }}
                />
                <SwitchTrack
                  className={formData.anonymous ? 'active' : ''}
                  style={{
                    backgroundColor: formData.anonymous
                      ? Colors.PrimaryBlue
                      : Colors.GrayLight,
                  }}
                >
                  <SwitchThumb
                    className={formData.anonymous ? 'active' : ''}
                    style={{
                      transform: formData.anonymous
                        ? 'translateX(24px)'
                        : 'translateX(2px)',
                    }}
                  />
                </SwitchTrack>
              </SwitchContainer>
              <AnonymousText>Anonymous</AnonymousText>
            </AnonymousLabel>
          </AnonymousSection>
        </ModalWrapper>

        <ButtonContainer>
          <Button
            onClick={handleSubmitAndReadPaper}
            disabled={isSubmitting}
            size="lg"
            fullWidth
          >
            {isSubmitting ? 'Sending...' : 'Read Paper'}
          </Button>
          <Button onClick={handleSkip} size="sm" secondary>
            Skip thank you note
          </Button>
        </ButtonContainer>
      </ModalContent>
    </Modal>
  );
};
